import React, { useState, useEffect, useMemo } from 'react';
import { Popover, Divider, Flex, Typography, Skeleton } from 'antd';
import DailyStatisticsCustomData from 'modules/ReportingModule/pages/InnerReportingPage/pages/dashboard/DailyStatisticsCustomData';
import RangePicker from 'components/RangePicker';
import useQueryParams from 'hooks/useQueryParams';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import getFormattedValue from 'store/redux/utils/getFormattedValue';
import ninjaApi from 'store/redux/apis';
import { mdiInformation } from '@mdi/js';
import InnerReportingBreadCrumbs from 'modules/ReportingModule/pages/InnerReportingPage/components/InnerReportingBreadCrumbs';
import { NameDefinition } from 'components/NameDefinition';
import AdsetAreaChart from '../AdsetAreaChart';
import { RoasFormatter, RoiFormatter } from 'components/tables/NinjaTable/formatters';
import Icon from '@mdi/react';
import Ninja from '@ninja';

const DATE_PICKER_WIDTH = 'w-36';

const InnerAdset = (props) => {
  const { id, adset } = props;
  const { searchParams } = useQueryParams();
  const { date_from = null, date_to = null } = searchParams;
  const [open, setOpen] = useState(false);
  const [dates, setDates] = useState({ date_from, date_to });

  const [getAdsetTotalsQuery, { data: total, isLoading: loadingTotal }] =
    ninjaApi.useLazyAdsetTotalsQuery();

  const [adsetDailyStatisticsDataQuery, { data: daily, isLoading: loadingDailyData, isFetching }] =
    ninjaApi.useLazyAdsetDailyStatisticsDataQuery();

  useEffect(() => {
    if (open && dates && id) {
      const filters = { ...dates, adset_id: id };
      getAdsetTotalsQuery({ filters });
      adsetDailyStatisticsDataQuery({ filters });
    }
  }, [id, open, dates, getAdsetTotalsQuery, adsetDailyStatisticsDataQuery]);

  const totals = useMemo(() => daily?.totals?.[0] || {}, [daily]);

  const stats = useMemo(
    () => [
      {
        title: 'Ad Clicks',
        name: 'clicks',
        render: (v) => getFormattedNumber(v),
      },
      {
        title: 'CPC',
        name: 'cpc',
        definition: <NameDefinition definition='Cost Per Click' />,
        render: (v) => getFormattedValue(v),
      },
      {
        title: 'CPA',
        name: 'cpa',
        definition: <NameDefinition definition='Cost Per Acquisition' />,
        render: (v) => getFormattedValue(v),
      },
      {
        title: 'Impressions',
        name: 'impressions',
        render: (v) => getFormattedNumber(v),
      },
      {
        title: 'Sessions',
        name: 'sessions',
        render: (v) => getFormattedNumber(v),
      },
      {
        title: 'Deals count',
        name: 'deals_count',
        render: (v) => getFormattedNumber(v),
      },
      {
        title: 'ROI',
        name: 'roi',
        definition: <NameDefinition definition='Return on Investment: Net Profit / Ad Spent' />,
        render: (v) => <RoiFormatter value={v || 0} fontSize={18} />,
      },
      {
        title: 'ROAS',
        name: 'roas',
        definition: <NameDefinition definition='Return on Ad Spent: Gross Income / Ad Spent' />,
        render: (v) => <RoasFormatter value={v} fontSize={18} />,
      },
    ],
    []
  );

  const renderStatCard = (stat) => {
    const statData = total?.[stat.name];

    return (
      <div
        key={stat.name}
        className='border-color-xlight/60 border-solid border-[1px] flex flex-col justify-between w-full h-[60px] rounded-lg p-2'
      >
        <Flex justify='space-between' align='center'>
          <Typography className='font-normal !text-xs text-color-dark-text'>
            {stat.title}
          </Typography>
          {stat.definition && stat.definition}
        </Flex>
        {loadingTotal ? (
          <Skeleton.Input size='small' active />
        ) : (
          <Typography className='!text-lg font-medium'>
            {stat.render ? stat.render(statData) : statData}
          </Typography>
        )}
      </div>
    );
  };

  const popoverContent = (
    <div className='sm:h-full h-screen overflow-auto grid gap-2 max-w-[800px] sm:p-0 p-4'>
      <Flex align='center' gap='small' justify='space-between' className='sm:flex-nowrap flex-wrap'>
        <Flex align='center' gap='small'>
          <Typography className='text-sm font-medium text-color-dark-text'>
            {adset?.name}
          </Typography>
          <Popover
            placement='bottom'
            destroyTooltipOnHide
            content={<InnerReportingBreadCrumbs dates={dates} id={id} />}
          >
            <Icon path={mdiInformation} className='cursor-pointer' color={Ninja.colors.darkText} />
          </Popover>
        </Flex>
        <RangePicker
          date={dates}
          onChange={setDates}
          datePickerProps={{
            size: 'small',
            className: `rounded-full ${DATE_PICKER_WIDTH}`,
          }}
        />
      </Flex>

      <Divider className='my-1' />

      <div className='grid md:grid-cols-4 sm:grid-cols-3 grid-cols-1 items-center gap-2 w-full'>
        {stats.map(renderStatCard)}
      </div>

      <div className='grid sm:grid-cols-2 grid-cols-1 gap-4'>
        <AdsetAreaChart
          dataKey='ad_spent'
          data={daily?.data}
          loading={isFetching}
          name='Ad Spent'
          color='#eb2f20'
          total={<span className='text-error'>{getFormattedValue(totals.ad_spent)}</span>}
        />
        <AdsetAreaChart
          dataKey='gross_income'
          data={daily?.data}
          loading={isFetching}
          name='Gross Income'
          color='#91c716'
          total={getFormattedValue(totals.gross_income)}
        />
      </div>
      <div style={{ maxWidth: 800, width: '100%' }}>
        <DailyStatisticsCustomData
          defaultFilters={dates}
          data={daily?.data}
          totals={totals}
          loading={loadingDailyData || isFetching}
        />
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement='right'
      trigger='click'
      content={popoverContent}
    >
      {props.children}
    </Popover>
  );
};

export default InnerAdset;
